import React, { FC, InputHTMLAttributes, useEffect, useRef } from 'react';

import classNames from 'classnames';
import { Input as AriaInput } from 'react-aria-components/Input';
import { useIntl } from 'react-intl';

import styles from './input-entry.module.scss';

import { KEY_CODE } from 'enums';
import { VoidFunctionNoArgs } from 'types';
import { Button } from 'views/components/UI/Button';
import { Enter } from 'views/components/UI/Icons/Icons';

interface IInputEntryProps extends InputHTMLAttributes<HTMLInputElement> {
  itemPadding?: string;
  minWidth?: string;
  backgroundColor?: string;
  classNameWrapper?: string;
  classNameField?: string;
  border?: boolean;
  disabledButton?: boolean;
  onClick?: VoidFunctionNoArgs;
  resetForm: VoidFunctionNoArgs;
  handleClose: VoidFunctionNoArgs;
  error?: boolean;
}

const InputEntry: FC<IInputEntryProps> = props => {
  const {
    type = 'text',
    placeholder,
    border,
    minWidth,
    itemPadding = '0.5rem 1.5rem',
    backgroundColor = 'var(--sk-background-surface)',
    disabledButton,
    classNameWrapper,
    classNameField,
    onClick,
    resetForm,
    handleClose,
    error,
    'aria-label': ariaLabel,
    ...restProps
  } = props;
  const intl = useIntl();
  const formRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === KEY_CODE.ESCAPE) {
        resetForm();
        handleClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (formRef.current && !event.composedPath().includes(formRef.current)) {
        resetForm();
        handleClose();
      }
    };

    document.body.addEventListener('click', handleClickOutside);

    return () => {
      document.body.removeEventListener('click', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const inputEntryClassNames = classNames(styles.new, classNameWrapper, {
    [styles.border]: border,
  });

  const inputFieldClassNames = classNames(styles.new__field, classNameField);

  const inputEntryStyles = {
    minWidth,
    padding: itemPadding,
    backgroundColor,
  };

  return (
    <div style={inputEntryStyles} className={inputEntryClassNames} ref={formRef}>
      <AriaInput
        ref={inputRef}
        className={inputFieldClassNames}
        type={type}
        aria-invalid={error || undefined}
        aria-label={
          ariaLabel ?? (placeholder ? intl.formatMessage({ id: placeholder }) : undefined)
        }
        placeholder={placeholder ? intl.formatMessage({ id: placeholder }) : ''}
        {...restProps}
      />
      <Button
        type="submit"
        icon={<Enter />}
        size="small"
        color="dark"
        mode="tertiary"
        onClick={onClick}
        disabled={disabledButton}
        aria-label={intl.formatMessage({ id: 'common.submit' })}
      />
    </div>
  );
};

export { InputEntry };
